// Spec 065 — Per-outcome live-trades overlay. Renders one transient bubble
// per recent trade whose `outcomeIndex` matches AND whose rowKey arrived
// via NATS after the parent's `useGetTrades` hook mounted (provenance
// owned by the SDK's `liveSinceMountIds` projection — see
// packages/backend-sdk-v2/src/nats/live-since-mount-store.ts).
//
// QUEUED RELEASE (supersedes the old "stream" model):
// Trades no longer all render at once. The old overlay rendered every
// matching trade simultaneously at the same fixed anchor, so trades arriving
// together stacked into an unreadable pile of overlapping particles. The
// overlay now owns a per-outcome RELEASE QUEUE: incoming trades are enqueued
// and released ONE AT A TIME on an internal timer. Each released bubble plays
// its full ~900ms drift+fade animation; the next bubble in the queue mounts
// only AFTER the current one has cleared the anchor (stagger interval =
// bubble lifetime + a small gap). Consecutive bubbles therefore read as a
// clean sequence and never visually collide.
//
// "Live since mount" provenance is OWNED BY THE SDK, not this overlay.
// Previously the overlay reinvented a `seenIds` ref and seeded it on first
// render — but the ref started empty, so every row in the REST snapshot
// animated as if freshly arrived (the cold-start bug: refresh /markets
// while bots are off and still see bubbles). The fix lives in the SDK:
// `useGetTrades({…}).liveSinceMountIds` is an identity-stable subset of
// `ids` containing only rowKeys tagged by a NATS-driven `prepend:` recipe
// AFTER the parent hook's snapshot landed. This overlay just filters the
// caller-supplied `trades` array to that set + the matching outcome.
//
// The queue is pure presentation state (a small state array + timers)
// inside this component.
//
// Data: the overlay is purely presentational. The caller passes (a) a
// `trades` array — the full mapped tape — and (b) `liveSinceMountIds` from
// the same `useGetTrades` result. The overlay intersects the two and
// filters by `outcomeIndex`, feeding the ordered live list to the release
// queue. Feed cards receive both from one route-level trade query.
//
// See `frontend/specs/065-live-trades/supplementary/contracts/live-trades-overlay.contract.md`.

'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, useReducedMotion } from 'motion/react';
import { useEffect, useReducer, useRef } from 'react';
import { MARKET_TRADES_TAPE_FIELDS } from '@/lib/sdk/projections';
import { LiveTradeBubble, STREAM_DURATION_S } from './live-trade-bubble';
import type { LiveTrade } from './types';

// Re-export so consumers can import the type alongside the component.
export type { LiveTrade } from './types';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface LiveTradesOverlayProps {
  /** The outcome's `originalIndex` (matches `OutcomeRowSummary.originalIndex`). */
  outcomeIndex: number;
  /**
   * Tailwind text-color class for this outcome — already resolved by the
   * parent via `useOutcomeColor`. Passed through to each bubble.
   */
  colorClass: string;
  /**
   * Spec 067 — horizontal anchor edge inside the parent positioning context.
   *
   * - `'right'` (default): wrapper sits at `bottom-1 right-2`; bubbles drift
   *   upward and stay right-aligned.
   * - `'left'`: wrapper sits at `bottom-1 left-2`; bubbles drift upward and
   *   stay left-aligned. Used by the spec 067 binary competing-bar variant.
   */
  anchor?: 'left' | 'right';
  /**
   * The card's live-trade tape, mapped to `LiveTrade[]` by the parent (the
   * feed's `FeedBarGrid` / the hero's `OutcomeSparklineGrid`, both via
   * `tradesRowsToLiveTrades`; or a caller-owned fixture array). The overlay
   * filters to entries where `trade.outcomeIndex === outcomeIndex` AND whose
   * id is in `liveSinceMountIds`, and feeds them to the release queue.
   */
  trades: readonly LiveTrade[];
  /**
   * The SDK-owned "live since mount" projection from the parent's
   * `useGetTrades(...)` result. Identity-stable across renders that don't
   * add/remove a fresh-arrival rowKey. The overlay treats this as the
   * authoritative "what's new" set — never falls back to the bare `trades`
   * array, which includes the REST snapshot of historical trades. Pass an
   * empty array for caller-owned fixtures (tests/stories where nothing is
   * "freshly arrived" by definition).
   */
  liveSinceMountIds: readonly string[];
}

/**
 * Release-queue cap — maximum number of PENDING (not-yet-shown) trades held
 * in the queue at once.
 *
 * Rationale: a release queue plays one bubble at a time at ~1s cadence, so it
 * can only "drain" ~1 trade/sec. Under a trade firehose (devnet bots land
 * ~1+ trades/sec; production could be far more) an unbounded queue would back
 * up indefinitely and the bubbles would lag minutes behind the real tape —
 * showing stale trades. Capping the pending queue keeps the displayed bubbles
 * near-real-time and bounds memory.
 *
 * Coalescing policy: when the cap is exceeded we DROP THE OLDEST pending
 * trades (keep the most recent `MAX_PENDING`). Newest trades are the most
 * relevant; the bubbles are a decorative "recent activity" pulse, not an
 * audit log, so silently dropping the oldest backlog under a flood is the
 * correct trade-off. Dropped trades are never animated — this is the single
 * documented exception to "never drop a real trade".
 *
 * 6 ≈ ~6s of backlog at the ~1s release cadence — enough to smooth a short
 * burst, short enough that the queue never visibly lags.
 */
const MAX_PENDING = 6;

/**
 * Stagger interval (ms) between consecutive bubble releases. Must be ≥ the
 * bubble's visible lifetime so the current bubble has fully cleared the
 * anchor (faded to opacity 0) before the next one mounts — otherwise they
 * overlap, which is the exact bug this queue fixes. We use the bubble's
 * actual animation duration (`STREAM_DURATION_S`) plus a small breathing gap.
 */
const RELEASE_GAP_MS = 120;
const RELEASE_INTERVAL_MS: number = Math.round(STREAM_DURATION_S * 1000) + RELEASE_GAP_MS;
interface BubbleReleaseState {
  readonly pending: readonly LiveTrade[];
  readonly current: LiveTrade | null;
}
type BubbleReleaseAction = {
  readonly type: 'enqueue';
  readonly trades: readonly LiveTrade[];
} | {
  readonly type: 'snap';
  readonly trade: LiveTrade | null;
} | {
  readonly type: 'promote';
} | {
  readonly type: 'retire';
};
const INITIAL_BUBBLE_RELEASE_STATE: BubbleReleaseState = {
  pending: [],
  current: null
};
function reduceBubbleRelease(state: BubbleReleaseState, action: BubbleReleaseAction): BubbleReleaseState {
  if (action.type === 'enqueue') {
    const next = [...state.pending, ...action.trades];
    return {
      ...state,
      pending: next.length > MAX_PENDING ? next.slice(-MAX_PENDING) : next
    };
  }
  if (action.type === 'snap') {
    return {
      ...state,
      current: action.trade
    };
  }
  if (action.type === 'promote') {
    const [current, ...pending] = state.pending;
    return {
      current: current ?? null,
      pending
    };
  }
  return {
    ...state,
    current: null
  };
}

/**
 * Field set for the live-trade bubbles overlay (FQ-097). Feed cards receive
 * this projection from `MarketFeedTradeProvider`; market detail shares the
 * same fields with its chart's per-outcome live tick line.
 *
 * - `eventId` is the list `rowKey`
 * - `outcomeIndex` / `type` / `usdcAmount` drive the overlay bubble + label
 * - `priceAfter` / `timestamp` drive the Live chart line
 */
// Single source of truth lives in the SSR-shared projections module so the
// chart card's `allTradesQuery` and the market-detail SSR prefetch key the
// same `useGetTrades` cache entry (byte-match — see `market-detail.ts`).
export const OVERLAY_TRADE_FIELDS: typeof MARKET_TRADES_TAPE_FIELDS = MARKET_TRADES_TAPE_FIELDS;
const ANCHOR_CLASS = {
  left: 'left-2',
  right: 'right-2'
} as const;

// ---------------------------------------------------------------------------
// Release queue hook
// ---------------------------------------------------------------------------

/**
 * Sequential release queue for live-trade bubbles.
 *
 * Input: `candidates` — the SDK-projected list of post-mount live trades that
 * match this outcome, in oldest → newest order. The SDK's
 * `liveSinceMountIds` projection guarantees every entry here arrived via a
 * NATS-driven `prepend:` recipe AFTER the parent's `useGetTrades` mounted —
 * historical REST-snapshot rows never appear, so this hook no longer needs
 * a `seenIds` ref or a mount-time seed effect.
 *
 * Behaviour:
 *  - As new ids appear in `candidates`, they are enqueued. The queue is
 *    capped at `MAX_PENDING` — oldest pending entries are dropped when the
 *    cap is exceeded (see `MAX_PENDING` rationale).
 *  - Exactly ONE bubble is "current" at a time. On a timer of
 *    `RELEASE_INTERVAL_MS` the current bubble is retired and the next pending
 *    trade is promoted. Because the interval ≥ the bubble's animation
 *    lifetime, the retiring bubble has already faded out before the next one
 *    mounts — no visual overlap.
 *  - Reduced motion: no timers; the most-recent matching trade is shown
 *    directly (still exactly one, still no overlap).
 *
 * Returns the single trade to render right now (or `null` when idle).
 */
function useBubbleReleaseQueue(candidates, reduceMotion) {
  const $ = _c(18);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const enqueuedIds = useRef(t0);
  const [t1, dispatch] = useReducer(reduceBubbleRelease, INITIAL_BUBBLE_RELEASE_STATE);
  const {
    pending,
    current
  } = t1;
  let t2;
  let t3;
  if ($[1] !== candidates || $[2] !== reduceMotion) {
    t2 = () => {
      const candidateIds = new Set(candidates.map(_temp));
      for (const id of enqueuedIds.current) {
        if (!candidateIds.has(id)) {
          enqueuedIds.current.delete(id);
        }
      }
      const newTrades = [];
      for (const t_0 of candidates) {
        if (enqueuedIds.current.has(t_0.id)) {
          continue;
        }
        enqueuedIds.current.add(t_0.id);
        newTrades.push(t_0);
      }
      if (newTrades.length === 0) {
        return;
      }
      if (reduceMotion) {
        dispatch({
          type: "snap",
          trade: newTrades[newTrades.length - 1] ?? null
        });
        return;
      }
      dispatch({
        type: "enqueue",
        trades: newTrades
      });
    };
    t3 = [reduceMotion, candidates];
    $[1] = candidates;
    $[2] = reduceMotion;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== current || $[6] !== pending.length || $[7] !== reduceMotion) {
    t4 = () => {
      if (reduceMotion) {
        return;
      }
      if (pending.length === 0 && current === null) {
        return;
      }
      const timer = setTimeout(() => {
        if (pending.length > 0) {
          dispatch({
            type: "promote"
          });
        } else {
          dispatch({
            type: "retire"
          });
        }
      }, RELEASE_INTERVAL_MS);
      return () => {
        clearTimeout(timer);
      };
    };
    $[5] = current;
    $[6] = pending.length;
    $[7] = reduceMotion;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== current || $[10] !== pending || $[11] !== reduceMotion) {
    t5 = [reduceMotion, pending, current];
    $[9] = current;
    $[10] = pending;
    $[11] = reduceMotion;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[13] !== current || $[14] !== pending.length || $[15] !== reduceMotion) {
    t6 = () => {
      if (reduceMotion || current !== null || pending.length === 0) {
        return;
      }
      dispatch({
        type: "promote"
      });
    };
    t7 = [reduceMotion, current, pending.length];
    $[13] = current;
    $[14] = pending.length;
    $[15] = reduceMotion;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  return current;
}

// ---------------------------------------------------------------------------
// Body — release queue over the caller-supplied array.
// ---------------------------------------------------------------------------
function _temp(t) {
  return t.id;
}
interface LiveTradesBodyProps {
  trades: readonly LiveTrade[];
  liveSinceMountIds: readonly string[];
  outcomeIndex: number;
  colorClass: string;
  reduceMotion: boolean;
  anchor: 'left' | 'right';
}
function LiveTradesBody(props) {
  const $ = _c(16);
  const {
    trades,
    liveSinceMountIds,
    outcomeIndex,
    colorClass,
    reduceMotion,
    anchor
  } = props;
  let t0;
  if ($[0] !== liveSinceMountIds) {
    t0 = new Set(liveSinceMountIds);
    $[0] = liveSinceMountIds;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const liveSet = t0;
  let t1;
  if ($[2] !== liveSet || $[3] !== outcomeIndex || $[4] !== trades) {
    let t2;
    if ($[6] !== liveSet || $[7] !== outcomeIndex) {
      t2 = t => liveSet.has(t.id) && t.outcomeIndex === outcomeIndex;
      $[6] = liveSet;
      $[7] = outcomeIndex;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = trades.filter(t2);
    $[2] = liveSet;
    $[3] = outcomeIndex;
    $[4] = trades;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const candidates = t1;
  const current = useBubbleReleaseQueue(candidates, reduceMotion);
  let t2;
  if ($[9] !== anchor || $[10] !== colorClass || $[11] !== current || $[12] !== reduceMotion) {
    t2 = current !== null ? <LiveTradeBubble key={current.id} trade={current} colorClass={colorClass} reduceMotion={reduceMotion} anchor={anchor} /> : null;
    $[9] = anchor;
    $[10] = colorClass;
    $[11] = current;
    $[12] = reduceMotion;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== t2) {
    t3 = <AnimatePresence>{t2}</AnimatePresence>;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LiveTradesOverlay(props) {
  const $ = _c(11);
  const {
    outcomeIndex,
    colorClass,
    anchor: t0,
    trades,
    liveSinceMountIds
  } = props;
  const anchor = t0 === undefined ? "right" : t0;
  const reduceMotion = useReducedMotion() === true;
  const t1 = `absolute bottom-1 ${ANCHOR_CLASS[anchor]} pointer-events-none z-10`;
  const t2 = `live-trades-${String(outcomeIndex)}`;
  let t3;
  if ($[0] !== anchor || $[1] !== colorClass || $[2] !== liveSinceMountIds || $[3] !== outcomeIndex || $[4] !== reduceMotion || $[5] !== trades) {
    t3 = <LiveTradesBody trades={trades} liveSinceMountIds={liveSinceMountIds} outcomeIndex={outcomeIndex} colorClass={colorClass} reduceMotion={reduceMotion} anchor={anchor} />;
    $[0] = anchor;
    $[1] = colorClass;
    $[2] = liveSinceMountIds;
    $[3] = outcomeIndex;
    $[4] = reduceMotion;
    $[5] = trades;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div aria-hidden="true" className={t1} data-testid={t2}>{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}